import React, { useState } from "react";
import { Heart, HandHeart, Shield, Users, ArrowRight, Loader2, Sparkles, Quote, Home as HomeIcon, GraduationCap } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import VeteranIntakeForm from "./VeteranIntakeForm";
import { useDocumentSeo } from "../lib/seo";

const PACKAGES = [
    { id: "seed", amount: 25, label: "Seed of Hope", description: "Provides a starter resource kit." },
    { id: "branch", amount: 50, label: "Branch of Restoration", description: "Sponsors one day of training." },
    { id: "harvest", amount: 100, label: "Harvest of Hope", description: "Funds professional clothing & supplies." },
    { id: "legacy", amount: 250, label: "Legacy Builder", description: "Supports one week of OJT mentorship." },
    { id: "covenant", amount: 500, label: "Covenant Partner", description: "Sponsors a full month of placement support." },
];

const TESTIMONIALS = [
    {
        name: "Marissa T.",
        role: "U.S. Army Veteran · Mother of Two",
        quote: "I served my country and somehow found myself sleeping in my car with my babies. Christina and this program didn't just hand me a job — they handed me my dignity back. I have an apartment, steady work, and my children sleep under a roof tonight. YHWH sent these people to me.",
        photo: "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
    {
        name: "David R.",
        role: "U.S. Marine Corps Veteran · OJT Graduate",
        quote: "After Afghanistan I lost everything — work, marriage, my apartment. I was on the street for fourteen months. The E.M.P.O.W.E.R.I.N.G program got me into temporary housing the same week I called, helped me earn my admin certification online, and stood with me until I had a real paycheck. I'm in my own place now. I sleep at night.",
        photo: "https://images.unsplash.com/photo-1639747280804-dd2d6b3d88ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
    {
        name: "Pastor Anthony W.",
        role: "Partner Employer · Dallas, TX",
        quote: "We hired two graduates from the E.M.P.O.W.E.R.I.N.G initiative last spring. Both are now in leadership tracks. This isn't charity hiring — these veterans bring discipline, faith, and excellence. We will keep hiring from this program as long as Christina keeps sending them.",
        photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
    {
        name: "Sister Karen M.",
        role: "Monthly Donor · Covenant Partner",
        quote: "I give every month because I have watched what Adonai does through Admin This! N Thatz Syncd. I have met the veterans. I have hugged their children. Every dollar I send walks straight from my checkbook into a veteran's apartment, classroom, or paycheck. There is no better stewardship of my giving.",
        photo: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    },
];

export default function Workforce() {
    useDocumentSeo({ title: "E.M.P.O.W.E.R.I.N.G Veterans Workforce Initiative — TT Admins", noindex: true });
    const [selected, setSelected] = useState("harvest");
    const [custom, setCustom] = useState("");
    const [donor, setDonor] = useState({ name: "", email: "" });
    const [loading, setLoading] = useState(null); // 'stripe' | 'paypal' | null

    const donate = async (provider) => {
        setLoading(provider);
        try {
            const payload = {
                origin_url: window.location.origin,
                purpose: "donation",
                donor_name: donor.name,
                donor_email: donor.email,
            };
            if (selected === "custom") {
                const amt = parseFloat(custom);
                if (!amt || amt < 5) {
                    toast.error("Please enter at least $5.");
                    setLoading(null);
                    return;
                }
                payload.custom_amount = amt;
            } else {
                payload.package_id = selected;
            }
            const endpoint = provider === "paypal" ? "/paypal/order" : "/checkout/session";
            const { data } = await api.post(endpoint, payload);
            window.location.href = data.url;
        } catch (e) {
            toast.error("Could not start donation. Please try again.");
            setLoading(null);
        }
    };

    return (
        <div data-testid="workforce-page">
            {/* Hero */}
            <section className="relative night-wash text-white grain overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center relative">
                    <div className="lg:col-span-7 fade-up">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
                            <Sparkles className="w-4 h-4 text-amber-300" />
                            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-amber-100/90">E.M.P.O.W.E.R.I.N.G Veterans Workforce Initiative</span>
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] font-light">
                            From the streets to a <span className="italic text-amber-300">steady paycheck</span>. Every veteran. Every family.
                        </h1>
                        <p className="mt-6 text-lg lg:text-xl text-slate-200 max-w-2xl leading-relaxed">
                            We walk alongside ALL homeless veterans &mdash; with or without children &mdash; connecting them to temporary housing, permanent housing pathways, online certification training, and the daily essentials needed to focus on rebuilding their lives.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <a href="#donate" className="btn-gold" data-testid="workforce-hero-donate-btn">
                                <Heart className="w-4 h-4 mr-2" /> Give a Gift of Hope
                            </a>
                            <a href="#program" className="btn-outline-navy !text-white !border-white/40 hover:!bg-white hover:!text-[var(--brand-navy)]">Learn the Program</a>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative fade-up delay-2">
                        <div className="relative aspect-square rounded-[2rem] overflow-hidden shadow-2xl">
                            <img src="https://images.pexels.com/photos/33963829/pexels-photo-33963829.jpeg" alt="Hands joined" className="w-full h-full object-cover" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission stats */}
            <section className="bg-white border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-10 text-center">
                    {[
                        { num: "33,000+", label: "U.S. veterans experience homelessness on any given night — many with children." },
                        { num: "100%", label: "of every dollar donated funds housing, training, and essentials for veteran families." },
                        { num: "VA + HUD", label: "official partnership for housing and workforce placement." },
                    ].map((s, i) => (
                        <div key={i} className={`fade-up delay-${i + 1}`}>
                            <div className="text-5xl font-serif font-light text-[var(--brand-coral)] mb-2">{s.num}</div>
                            <div className="text-sm text-slate-600 max-w-xs mx-auto leading-relaxed">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Mission / Vision / Purpose */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                    {[
                        { icon: <Shield className="w-7 h-7" />, title: "Our Mission", body: "To restore hope, opportunity, and home for ALL homeless veterans and their children — anchoring restoration in faith, dignity, and skill." },
                        { icon: <Sparkles className="w-7 h-7" />, title: "Our Vision", body: "A nation where no veteran or their family sleeps without shelter, no child goes without a classroom, and every life finds work that honors Elohim." },
                        { icon: <HandHeart className="w-7 h-7" />, title: "Our Purpose", body: "To remove the daily survival struggle so veterans can focus on training — funding housing, food, wardrobe, and essentials while parents earn certifications that lead to higher-wage administrative careers." },
                    ].map((c, i) => (
                        <div key={c.title} className={`faith-card p-8 fade-up delay-${i + 1}`} data-testid={`mission-card-${i}`}>
                            <div className="w-14 h-14 rounded-2xl bg-[var(--brand-coral)] text-white flex items-center justify-center mb-6">{c.icon}</div>
                            <h3 className="font-serif text-2xl text-[var(--brand-navy)] mb-3">{c.title}</h3>
                            <p className="text-slate-600 leading-relaxed text-sm">{c.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Four Pillars */}
            <section id="program" className="sky-wash py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-14">
                        <div className="eyebrow mb-3">The Three Pillars</div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)]">
                            How E.M.P.O.W.E.R.I.N.G changes a veteran's life.
                        </h2>
                        <p className="mt-4 text-slate-700 leading-relaxed text-lg max-w-2xl">
                            Every homeless veteran's path is different — and so are the federal and state programs they qualify for. We meet each veteran where they are, then walk with them across three pillars of restoration.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="three-pillars">
                        {[
                            {
                                icon: <HomeIcon className="w-7 h-7" />,
                                num: "01",
                                title: "Housing",
                                body: "Immediate connection to temporary shelter, then guided navigation to permanent housing through the right program for each veteran — VA, HUD-VASH, SSVF, GPD, and more.",
                                accent: "bg-[var(--brand-sky)]",
                            },
                            {
                                icon: <HandHeart className="w-7 h-7" />,
                                num: "02",
                                title: "Essentials",
                                body: "Professional wardrobe, food, toiletries, temp housing assistance, and daily-living stipends so the veteran can stop surviving and start rebuilding.",
                                accent: "bg-[var(--brand-coral)]",
                            },
                            {
                                icon: <GraduationCap className="w-7 h-7" />,
                                num: "03",
                                title: "Training",
                                body: "Online remote certification programs in administrative support — flexible enough to fit around housing transitions, equipping veterans for higher-wage roles.",
                                accent: "bg-[var(--brand-gold)]",
                            },
                        ].map((p, i) => (
                            <div key={p.title} className={`faith-card p-7 fade-up delay-${i + 1}`} data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                                <div className={`w-12 h-12 rounded-2xl ${p.accent} text-white flex items-center justify-center mb-5`}>{p.icon}</div>
                                <div className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">Pillar {p.num}</div>
                                <h3 className="font-serif text-2xl text-[var(--brand-navy)] mb-3">{p.title}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-14 grid md:grid-cols-12 gap-8 items-center bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-10">
                        <div className="md:col-span-7">
                            <div className="eyebrow mb-2">Where every dollar goes</div>
                            <h3 className="font-serif text-2xl sm:text-3xl text-[var(--brand-navy)] mb-3 leading-snug">
                                Donations and grants fund one unified mission.
                            </h3>
                            <p className="text-slate-600 leading-relaxed">
                                Every gift and every grant we receive flows into one fund — providing housing, professional wardrobes, daily essentials, food, and certification training for veteran families. One mission. Every dollar.
                            </p>
                        </div>
                        <div className="md:col-span-5">
                            <a href="#donate" className="btn-gold w-full" data-testid="pillars-donate-link">
                                Give a Gift of Hope <ArrowRight className="w-4 h-4 ml-2" />
                            </a>
                            <a href="/contact" className="btn-outline-navy w-full mt-3" data-testid="pillars-partner-link">
                                Become a Partner Employer
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Veteran Intake (Need Help?) */}
            <section id="get-help" className="bg-white border-y border-slate-200 py-20 lg:py-28" data-testid="intake-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-24">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--brand-coral)]/10 border border-[var(--brand-coral)]/30 mb-4">
                            <span className="w-2 h-2 rounded-full bg-[var(--brand-coral)] animate-pulse" />
                            <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-coral)]">Need Help Now?</span>
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)] mb-5">
                            You served us. <span className="italic text-[var(--brand-coral)]">Now let us serve you.</span>
                        </h2>
                        <p className="text-slate-700 leading-relaxed text-lg mb-4">
                            If you are a homeless veteran &mdash; with or without children &mdash; or if you are helping one, fill out this short form and we will call you back within <strong>24 hours</strong>.
                        </p>
                        <p className="text-slate-600 leading-relaxed">
                            No paperwork required to start. No judgment. No cost to you. Just a person who will pick up the phone, listen, and walk with you into the right program.
                        </p>
                        <div className="mt-7 space-y-3 text-sm">
                            <div className="flex items-start"><span className="text-[var(--brand-coral)] mr-2.5">•</span><span className="text-slate-700"><strong>Step 1:</strong> Submit this form or call us</span></div>
                            <div className="flex items-start"><span className="text-[var(--brand-coral)] mr-2.5">•</span><span className="text-slate-700"><strong>Step 2:</strong> We listen and identify which program you qualify for (VA, HUD-VASH, SSVF, etc.)</span></div>
                            <div className="flex items-start"><span className="text-[var(--brand-coral)] mr-2.5">•</span><span className="text-slate-700"><strong>Step 3:</strong> We walk with you through housing, essentials, and training</span></div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <VeteranIntakeForm />
                    </div>
                </div>
            </section>

            {/* Donate widget */}
            <section id="donate" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5 lg:sticky lg:top-24">
                        <div className="eyebrow mb-3">Give</div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)] mb-6">
                            Plant a seed of restoration today.
                        </h2>
                        <p className="text-slate-700 leading-relaxed mb-4">
                            Every gift — large or small — directly funds housing, training, professional wardrobes, food, and daily essentials for the veteran families enrolled in our program.
                        </p>
                        <p className="text-slate-600 leading-relaxed mb-6">
                            100% of every dollar goes directly to program services. We publish full transparency on where funds are allocated across the four pillars: housing, essentials, training, and kids' education.
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <Shield className="w-4 h-4 text-emerald-600" /> Secured by Stripe. Receipts emailed instantly.
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="faith-card p-8 sm:p-10" data-testid="donate-widget">
                            <h3 className="font-serif text-2xl text-[var(--brand-navy)] mb-6">Choose a gift</h3>
                            <div className="grid sm:grid-cols-2 gap-3 mb-6">
                                {PACKAGES.map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => setSelected(p.id)}
                                        className={`text-left rounded-2xl p-5 border-2 transition-all ${
                                            selected === p.id
                                                ? "border-[var(--brand-gold)] bg-[var(--brand-gold)]/10"
                                                : "border-slate-200 hover:border-slate-300 bg-white"
                                        }`}
                                        data-testid={`donate-pkg-${p.id}`}
                                    >
                                        <div className="text-2xl font-serif text-[var(--brand-navy)]">${p.amount}</div>
                                        <div className="font-semibold text-sm text-[var(--brand-navy)] mt-1">{p.label}</div>
                                        <div className="text-xs text-slate-600 mt-1">{p.description}</div>
                                    </button>
                                ))}
                                <button
                                    onClick={() => setSelected("custom")}
                                    className={`text-left rounded-2xl p-5 border-2 transition-all ${
                                        selected === "custom"
                                            ? "border-[var(--brand-gold)] bg-[var(--brand-gold)]/10"
                                            : "border-slate-200 hover:border-slate-300 bg-white"
                                    }`}
                                    data-testid="donate-pkg-custom"
                                >
                                    <div className="text-2xl font-serif text-[var(--brand-navy)]">$ —</div>
                                    <div className="font-semibold text-sm text-[var(--brand-navy)] mt-1">Custom Amount</div>
                                    <div className="text-xs text-slate-600 mt-1">Give as the Father leads.</div>
                                </button>
                            </div>

                            {selected === "custom" && (
                                <div className="mb-6">
                                    <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Amount (USD, min $5)</label>
                                    <input
                                        type="number"
                                        min="5"
                                        value={custom}
                                        onChange={(e) => setCustom(e.target.value)}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)]"
                                        placeholder="e.g. 75"
                                        data-testid="donate-custom-input"
                                    />
                                </div>
                            )}

                            <div className="grid sm:grid-cols-2 gap-4 mb-6">
                                <Field label="Your Name (optional)" testid="donor-name">
                                    <input
                                        value={donor.name}
                                        onChange={(e) => setDonor({ ...donor, name: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)]"
                                        data-testid="donor-name-input"
                                    />
                                </Field>
                                <Field label="Your Email (optional, for receipt)" testid="donor-email">
                                    <input
                                        type="email"
                                        value={donor.email}
                                        onChange={(e) => setDonor({ ...donor, email: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-gold)]"
                                        data-testid="donor-email-input"
                                    />
                                </Field>
                            </div>

                            <button onClick={() => donate("stripe")} disabled={!!loading} className="btn-gold w-full disabled:opacity-50" data-testid="donate-submit-btn">
                                {loading === "stripe" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : <><Heart className="w-4 h-4 mr-2" /> Give with Card</>}
                            </button>
                            <button onClick={() => donate("paypal")} disabled={!!loading} className="w-full mt-3 rounded-full px-7 py-3 font-semibold transition-all bg-[#FFC439] hover:bg-[#F0B400] text-[#003087] disabled:opacity-50 inline-flex items-center justify-center" data-testid="donate-paypal-btn">
                                {loading === "paypal" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : <>Give with <span className="italic font-bold ml-1.5"><span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span></span></>}
                            </button>
                            <div className="text-center text-xs text-slate-500 mt-4">
                                Secured by Stripe & PayPal. Receipts emailed instantly.
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials / Restoration Stories */}
            <section className="bg-[var(--brand-cream)] py-20 lg:py-28" data-testid="testimonials-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-14">
                        <div className="eyebrow mb-3">Restoration Stories</div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)]">
                            Lives being restored, one veteran at a time.
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed text-lg max-w-2xl">
                            These are the voices of women who walked through our doors weary and walked out renewed. Every word here is why we do what we do.
                        </p>
                        <p className="mt-3 text-xs text-slate-400 italic">
                            * Names changed and photos are representative to protect veteran privacy.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <article
                                key={t.name}
                                className={`faith-card p-8 sm:p-10 relative fade-up delay-${(i % 4) + 1}`}
                                data-testid={`testimonial-${i}`}
                            >
                                <Quote className="absolute top-6 right-6 w-10 h-10 text-[var(--brand-gold)]/30" />
                                <blockquote className="editorial-quote text-lg sm:text-xl leading-relaxed text-[var(--brand-navy)] mb-7">
                                    "{t.quote}"
                                </blockquote>
                                <div className="flex items-center gap-4 pt-5 border-t border-slate-100">
                                    <img src={t.photo} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-[var(--brand-gold)]/30" loading="lazy" />
                                    <div>
                                        <div className="font-serif text-base text-[var(--brand-navy)]">{t.name}</div>
                                        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{t.role}</div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* Volunteer / Partner */}
            <section className="bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid md:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="eyebrow mb-3">Beyond Giving</div>
                            <h2 className="font-serif text-4xl leading-tight text-[var(--brand-navy)]">Volunteer, mentor, or partner.</h2>
                            <p className="text-slate-600 mt-4 leading-relaxed">
                                The work of restoration takes a community. If you're an employer, a mentor, a counselor, or simply someone with hours to give — we have a place for you.
                            </p>
                            <a href="/contact" className="btn-outline-navy mt-8" data-testid="workforce-volunteer-btn">Get Involved <ArrowRight className="w-4 h-4 ml-2" /></a>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="aspect-square rounded-3xl overflow-hidden">
                                <img src="https://images.pexels.com/photos/1181738/pexels-photo-1181738.jpeg" alt="Women in business meeting" className="w-full h-full object-cover" />
                            </div>
                            <div className="aspect-square rounded-3xl overflow-hidden mt-8">
                                <img src="https://images.pexels.com/photos/5303560/pexels-photo-5303560.jpeg" alt="Family learning" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</span>
            {children}
        </label>
    );
}
